import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CheckCircle2, Gift, Calendar, CreditCard, HeartPulse, Salad } from "lucide-react";
import { Link } from "react-router-dom";
import { memo, useState, useCallback } from "react";

export const ProfessionalCTA = memo(() => {
  const [bgLoaded, setBgLoaded] = useState(false);
  
  const handleBgLoad = useCallback(() => {
    setBgLoaded(true);
  }, []);

  const benefits = [
    "90 días de prueba gratis",
    "Sistema de reservas incluido",
    "0% comisiones por sesión",
    "Panel de gestión completo",
    "Recordatorios automáticos",
    "Pagos online integrados",
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background - optimized with lazy loading */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1920&h=800&fit=crop&q=80"
          alt=""
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${bgLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          decoding="async"
          onLoad={handleBgLoad}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/90 to-primary/80" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              <Gift className="w-4 h-4 mr-2" />
              Oferta Especial
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              ¿Eres Profesional del Fitness o la Salud?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Únete a la plataforma líder y haz crecer tu negocio. 
              Perfil profesional, sistema de reservas y pagos online. Todo incluido.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-300 flex-shrink-0" />
                  <span className="text-white/90">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/become-trainer">
                <Button size="lg" variant="secondary" className="text-primary font-semibold">
                  Soy Entrenador
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/become-physiotherapist">
                <Button size="lg" className="bg-rose-500 hover:bg-rose-600 text-white whitespace-nowrap">
                  <HeartPulse className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>Soy Fisioterapeuta</span>
                </Button>
              </Link>
              <Link to="/become-nutritionist">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white whitespace-nowrap">
                  <Salad className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>Soy Nutricionista</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Right - Feature Cards */}
          <div className="hidden lg:block">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-xl bg-white/20">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Sistema de Reservas</h3>
                    <p className="text-white/80">Incluido gratis durante 90 días</p>
                  </div>
                </div>
                <p className="text-white/70">
                  Gestiona tu agenda, recibe reservas online y envía recordatorios automáticos. 
                  Valorado en 29€/mes.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-xl bg-white/20">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Pagos Integrados</h3>
                    <p className="text-white/80">Stripe, PayPal o efectivo</p>
                  </div>
                </div>
                <p className="text-white/70">
                  Cobra online de forma segura. Sin comisiones por transacción. 
                  Tú recibes el 100% de cada pago.
                </p>
              </div>

              <div className="flex items-center gap-4 p-4 bg-green-500/20 backdrop-blur-sm rounded-xl border border-green-400/30">
                <Gift className="w-8 h-8 text-green-300" />
                <div>
                  <p className="font-semibold text-white">Empieza GRATIS</p>
                  <p className="text-sm text-white/80">Sin tarjeta de crédito • Sin compromiso</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});